import { useState, useEffect, useRef } from 'react'
import {
  Search, Users, Map, FileText, Palette, Code, Image, Globe,
  ChevronDown, ChevronUp, Sparkles, CheckCircle2, Loader2, Clock, Lightbulb, Gamepad2
} from 'lucide-react'
import { PHASES } from '@agents/orchestrator'
import SnakeGame from './SnakeGame'

// Estimated time per phase (in seconds) - Updated for more context/tokens
const PHASE_ESTIMATES = {
  [PHASES.SERP]: { min: 15, max: 45 },
  [PHASES.STRATEGE]: { min: 45, max: 120 },
  [PHASES.IDENTITE]: { min: 30, max: 90 },
  [PHASES.ARCHITECTE]: { min: 30, max: 90 },
  [PHASES.REDACTEUR]: { min: 45, max: 120 },
  [PHASES.DA]: { min: 30, max: 90 },
  [PHASES.CONTENT]: { min: 90, max: 240 },
  [PHASES.IMAGES]: { min: 45, max: 180 },
}

// Phase-specific tips (effort bias - show what the agent is doing)
// More detailed tips that rotate every 8 seconds
const PHASE_TIPS = {
  [PHASES.SERP]: [
    "🔍 Interrogation des APIs de recherche en temps réel pour analyser votre marché",
    "📊 Extraction des 10 premiers résultats Google : qui sont vos vrais concurrents ?",
    "❓ Analyse des 'People Also Ask' : ce sont les vraies questions de vos futurs clients",
    "🎯 Identification des featured snippets : opportunités de position 0 à saisir",
    "📍 Cartographie locale : analyse des résultats géolocalisés pour votre zone",
    "🔗 Collecte des sources E-E-A-T pour enrichir votre contenu avec des références",
  ],
  [PHASES.STRATEGE]: [
    "🧠 Décodage sémantique du nom de domaine : extraction du métier, zone géographique, spécialités",
    "⚔️ Analyse concurrentielle : forces et faiblesses des sites en première page",
    "🎯 Définition de votre angle d'attaque unique pour vous différencier",
    "📈 Cartographie des mots-clés : volume de recherche, difficulté, potentiel de trafic",
    "💡 Identification des quick wins : opportunités de ranking rapide à cibler",
    "🏆 Positionnement stratégique : comment devenir LA référence sur votre marché",
  ],
  [PHASES.IDENTITE]: [
    "👤 Création du persona client idéal : qui cherche vos services ? Quels sont ses pain points ?",
    "🎭 Définition du ton éditorial : le bon équilibre entre expert et accessible",
    "💬 Construction de la brand voice : vocabulaire spécifique à votre secteur",
    "✨ Élaboration de vos arguments de vente uniques (USP) qui font la différence",
    "📖 Rédaction du backstory : l'histoire qui crée la confiance avec vos visiteurs",
    "🤝 Calibrage du niveau d'expertise affiché selon les attentes de votre cible",
  ],
  [PHASES.ARCHITECTE]: [
    "🏗️ Structure en silos thématiques : T1 (accueil) → T2 (catégories) → T3 (services) → T4 (détails)",
    "🔀 Conception du maillage interne : chaque page pousse les autres stratégiquement",
    "🤖 Optimisation pour le crawl budget Google : structure plate et efficace",
    "🔗 Définition des URLs SEO-friendly avec mots-clés cibles intégrés",
    "⚡ Planification des ancres de liens pour distribuer le jus SEO",
    "📱 Architecture responsive : navigation fluide sur mobile et desktop",
  ],
  [PHASES.REDACTEUR]: [
    "📝 Briefs détaillés par page : structure Hn, nombre de mots, densité de keywords",
    "❓ Génération des FAQs à partir des vraies questions Google (People Also Ask)",
    "🔮 Création des FLUQs : questions que vos futurs clients poseront demain",
    "🧬 Optimisation sémantique : mots-clés LSI, entités nommées, co-occurrences",
    "✍️ Rédaction des meta titles avec formules de CTR éprouvées",
    "📢 Meta descriptions engageantes qui donnent envie de cliquer",
  ],
  [PHASES.DA]: [
    "🎨 Palette de couleurs choisie selon la psychologie des couleurs de votre secteur",
    "🔤 Sélection typographique : lisibilité web optimale + personnalité de marque",
    "✨ Définition du style visuel : moderne, traditionnel, premium ou accessible",
    "📐 Cohérence graphique globale : espacements, bordures, ombres, micro-interactions",
    "📱 Design mobile-first : touch targets, tailles de police, contrastes WCAG",
    "🎯 Hiérarchie visuelle pour guider l'œil vers vos CTA",
  ],
  [PHASES.CONTENT]: [
    "💻 Génération HTML5 sémantique : header, main, article, section, footer, nav",
    "📊 Intégration des données structurées Schema.org LocalBusiness pour Google",
    "🎨 CSS optimisé avec variables custom, flexbox, grid et animations GPU",
    "📋 Balisage Hn hiérarchique : un seul H1, structure logique H2 → H6",
    "⚡ Optimisations Core Web Vitals : lazy loading, async scripts, critical CSS",
    "♿ Accessibilité WCAG : ARIA labels, focus states, contrastes suffisants",
  ],
  [PHASES.IMAGES]: [
    "🖼️ Extraction des balises img et analyse des besoins visuels par page",
    "✍️ Génération de prompts optimisés pour l'IA de génération d'images",
    "📦 Compression WebP intelligente : qualité visuelle max, poids < 100KB",
    "🏷️ Rédaction des alt text SEO : descriptifs, accessibles, avec mots-clés",
    "📐 Génération des dimensions responsives pour tous les breakpoints",
    "🎨 Cohérence stylistique entre toutes les images du site",
  ],
}

// Format seconds to mm:ss
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Agent identities matching PipelineProgress
const AGENT_IDENTITIES = {
  [PHASES.SERP]: {
    name: 'SERP Analyzer',
    icon: Globe,
    color: 'from-cyan-400 to-teal-500',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    textColor: 'text-cyan-400',
    role: 'Analyse Google en temps réel'
  },
  [PHASES.STRATEGE]: {
    name: 'Stratege',
    icon: Search,
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-400',
    role: 'Positionnement & mots-cles'
  },
  [PHASES.IDENTITE]: {
    name: 'Identite',
    icon: Users,
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    textColor: 'text-purple-400',
    role: 'Persona & ton editorial'
  },
  [PHASES.ARCHITECTE]: {
    name: 'Architecte',
    icon: Map,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    textColor: 'text-emerald-400',
    role: 'Structure & navigation'
  },
  [PHASES.REDACTEUR]: {
    name: 'Redacteur',
    icon: FileText,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    textColor: 'text-amber-400',
    role: 'Briefs SEO par page'
  },
  [PHASES.DA]: {
    name: 'Directeur Artistique',
    icon: Palette,
    color: 'from-rose-500 to-red-500',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30',
    textColor: 'text-rose-400',
    role: 'Charte graphique'
  },
  [PHASES.CONTENT]: {
    name: 'Content Writer',
    icon: Code,
    color: 'from-indigo-500 to-violet-500',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-400',
    role: 'Generation HTML/CSS'
  },
  [PHASES.IMAGES]: {
    name: 'Imagier',
    icon: Image,
    color: 'from-green-500 to-lime-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-400',
    role: 'Generation visuels'
  }
}

// Fallback identity
const DEFAULT_IDENTITY = {
  name: 'Agent IA',
  icon: Sparkles,
  color: 'from-primary-500 to-accent-500',
  bgColor: 'bg-primary-500/10',
  borderColor: 'border-primary-500/30',
  textColor: 'text-primary-400',
  role: 'Assistant'
}

export default function AgentMessage({ agent, summary, status = 'complete', metrics }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [currentTip, setCurrentTip] = useState(0)
  const [showSnake, setShowSnake] = useState(false)
  const timerRef = useRef(null)
  const startTimeRef = useRef(Date.now())

  const identity = AGENT_IDENTITIES[agent] || DEFAULT_IDENTITY
  const Icon = identity.icon
  const estimate = PHASE_ESTIMATES[agent]
  const tips = PHASE_TIPS[agent] || []

  const isRunning = status === 'running'
  const isComplete = status === 'complete'

  // Timer for running state
  useEffect(() => {
    if (isRunning) {
      startTimeRef.current = Date.now()
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000))
      }, 1000)
      return () => clearInterval(timerRef.current)
    } else {
      clearInterval(timerRef.current)
    }
  }, [isRunning])

  // Rotate tips every 8 seconds (slower, more readable)
  useEffect(() => {
    if (isRunning && tips.length > 0) {
      const tipInterval = setInterval(() => {
        setCurrentTip(prev => (prev + 1) % tips.length)
      }, 8000)
      return () => clearInterval(tipInterval)
    }
  }, [isRunning, tips.length])

  // Render markdown-like content
  const renderContent = (text) => {
    if (!text) return null
    let html = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>')
    html = html.replace(/`([^`]+)`/g, '<code class="bg-slate-700 px-1.5 py-0.5 rounded text-xs">$1</code>')
    html = html.replace(/\n/g, '<br />')
    return <span dangerouslySetInnerHTML={{ __html: html }} />
  }

  return (
    <div className="group max-w-3xl">
      <div className="flex gap-3">
        {/* Agent avatar with gradient */}
        <div className="relative flex-shrink-0">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${identity.color} p-0.5`}>
            <div className="w-full h-full rounded-[10px] bg-slate-900 flex items-center justify-center">
              {isRunning ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Icon className="w-5 h-5 text-white" />
              )}
            </div>
          </div>
          {/* Status indicator */}
          {isComplete && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center ring-2 ring-slate-900">
              <CheckCircle2 className="w-3 h-3 text-white" />
            </div>
          )}
          {isRunning && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center ring-2 ring-slate-900">
              <Sparkles className="w-3 h-3 text-white animate-pulse" />
            </div>
          )}
        </div>

        {/* Message content */}
        <div className="flex-1 min-w-0">
          {/* Header with name and role */}
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-semibold ${identity.textColor}`}>
              {identity.name}
            </span>
            <span className="text-xs text-slate-500">
              {identity.role}
            </span>
            {isRunning && (
              <span className="text-xs text-amber-400 animate-pulse">
                en cours...
              </span>
            )}
          </div>

          {/* Message bubble */}
          <div className={`rounded-2xl rounded-tl-sm ${identity.bgColor} border ${identity.borderColor} overflow-hidden`}>
            {/* Summary always visible */}
            {summary && (
              <div className="px-4 py-3">
                {/* Headline */}
                <div className="text-sm text-slate-200 leading-relaxed font-medium">
                  {renderContent(summary)}
                </div>

                {/* Rich metrics grid */}
                {metrics && metrics.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-700/50">
                    {metrics.map((m, i) => (
                      <div key={i} className={`flex items-center gap-2 bg-slate-800/30 rounded-lg px-2.5 py-1.5 ${m.isPalette ? 'col-span-2' : ''}`}>
                        {/* Color palette (multiple swatches) */}
                        {m.isPalette ? (
                          <div className="flex gap-1 flex-shrink-0">
                            {m.value.map((color, ci) => (
                              <div
                                key={ci}
                                className="w-5 h-5 rounded-md ring-1 ring-white/20"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                        ) : m.isColor ? (
                          <div
                            className="w-4 h-4 rounded-md ring-1 ring-white/20 flex-shrink-0"
                            style={{ backgroundColor: m.value }}
                            title={m.value}
                          />
                        ) : (
                          <span className="text-sm flex-shrink-0">{m.icon}</span>
                        )}
                        <div className="min-w-0 flex-1">
                          <span className="text-[10px] text-slate-500 uppercase tracking-wider">{m.label}</span>
                          <div className="text-xs text-white font-medium truncate" title={Array.isArray(m.value) ? m.value.join(' • ') : m.value}>
                            {m.isPalette ? m.value.join(' • ') : m.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Running state - rich with timer + tips + optional Snake! */}
            {isRunning && !summary && (
              <div className="px-4 py-3 space-y-3">
                {/* Timer row with Snake toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className={`w-4 h-4 ${identity.textColor}`} />
                    <span className="font-mono text-sm text-white">{formatTime(elapsedTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {estimate && (
                      <span className="text-xs text-slate-500">
                        ~{estimate.max < 60
                          ? `${estimate.min}-${estimate.max}s`
                          : `${Math.round(estimate.min/60)}-${Math.round(estimate.max/60)} min`
                        }
                      </span>
                    )}
                    <button
                      onClick={() => setShowSnake(!showSnake)}
                      className={`p-1 rounded transition-colors ${
                        showSnake
                          ? 'bg-primary-500/20 text-primary-400'
                          : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
                      }`}
                      title={showSnake ? 'Voir les tips' : 'Jouer au Snake!'}
                    >
                      <Gamepad2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Snake game OR tips */}
                {showSnake ? (
                  <div className="flex justify-center py-2">
                    <SnakeGame color="primary" />
                  </div>
                ) : (
                  /* Current tip with rotation indicator */
                  tips.length > 0 && (
                    <div className="flex items-start gap-2 p-2 bg-slate-800/50 rounded-lg">
                      <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-300 leading-relaxed">
                          {tips[currentTip]}
                        </p>
                        {/* Tip progress dots */}
                        <div className="flex gap-1 mt-2">
                          {tips.map((_, idx) => (
                            <div
                              key={idx}
                              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                idx === currentTip ? identity.textColor.replace('text-', 'bg-') : 'bg-slate-600'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export { AGENT_IDENTITIES }
